import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MeetingInformation = () => {
  const [activeTab, setActiveTab] = useState('location');

  const meetingSchedule = [
    {
      id: 1,
      type: "Regular Meeting",
      day: "Every 2nd & 4th Saturday",
      time: "2:00 PM - 4:00 PM",
      location: "Colombo Club, 5 Galle Face Terrace",
      description: "Our bi-weekly meetings for project planning and member updates",
      isRecurring: true
    },
    {
      id: 2,
      type: "Executive Board Meeting",
      day: "Every 1st Wednesday",
      time: "6:30 PM - 8:00 PM",
      location: "Leo Club Office, Level 3, Unity Plaza",
      description: "Leadership team strategic planning and decision making",
      isRecurring: true
    },
    {
      id: 3,
      type: "Community Service Day",
      day: "Every 3rd Sunday",
      time: "8:00 AM - 5:00 PM",
      location: "Various Community Locations",
      description: "Hands-on community service projects and activities",
      isRecurring: true
    },
    {
      id: 4,
      type: "New Member Orientation",
      day: "January 28, 2025",
      time: "10:00 AM - 12:00 PM",
      location: "Colombo Club, 5 Galle Face Terrace",
      description: "Welcome session for new members joining this quarter",
      isRecurring: false
    }
  ];

  const locationDetails = {
    address: "Colombo Club, 5 Galle Face Terrace, Colombo 03, Sri Lanka",
    coordinates: "6.9271,79.8612",
    parking: "Free parking available in the club premises",
    publicTransport: "Bus routes 100, 101, 102 stop nearby. Galle Face Green bus stop is 2 minutes walk.",
    accessibility: "Wheelchair accessible entrance and facilities available",
    landmarks: "Located opposite Galle Face Green, next to Taj Samudra Hotel"
  };

  const tabs = [
    { id: 'location', label: 'Location & Directions', icon: 'MapPin' },
    { id: 'schedule', label: 'Meeting Schedule', icon: 'Calendar' },
    { id: 'guest', label: 'Guest Information', icon: 'Users' }
  ];

  const handleRSVP = (meetingType) => {
    window.open(`mailto:meetings@leoclubcolombo.lk?subject=RSVP for ${meetingType}&body=I would like to attend the ${meetingType}. Please confirm my attendance.`, '_blank');
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Meeting Information
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join us in person to experience our community of young leaders
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center mb-12">
          <div className="bg-surface rounded-lg p-1 inline-flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium transition-smooth ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon} size={18} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Location Tab */}
        {activeTab === 'location' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-8 card-shadow">
                <h3 className="text-xl font-semibold text-card-foreground mb-6 flex items-center gap-3">
                  <Icon name="MapPin" size={24} className="text-primary" />
                  Primary Meeting Location
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Building" size={20} className="text-muted-foreground mt-1" />
                    <div>
                      <p className="font-medium text-card-foreground">Address</p>
                      <p className="text-muted-foreground">{locationDetails.address}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Icon name="Car" size={20} className="text-muted-foreground mt-1" />
                    <div>
                      <p className="font-medium text-card-foreground">Parking</p>
                      <p className="text-muted-foreground">{locationDetails.parking}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Icon name="Bus" size={20} className="text-muted-foreground mt-1" />
                    <div>
                      <p className="font-medium text-card-foreground">Public Transport</p>
                      <p className="text-muted-foreground">{locationDetails.publicTransport}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Icon name="Accessibility" size={20} className="text-muted-foreground mt-1" />
                    <div>
                      <p className="font-medium text-card-foreground">Accessibility</p>
                      <p className="text-muted-foreground">{locationDetails.accessibility}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Icon name="Landmark" size={20} className="text-muted-foreground mt-1" />
                    <div>
                      <p className="font-medium text-card-foreground">Landmarks</p>
                      <p className="text-muted-foreground">{locationDetails.landmarks}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-border">
                  <Button
                    variant="outline"
                    iconName="Navigation"
                    iconPosition="left"
                    onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${locationDetails.coordinates}`, '_blank')}
                    fullWidth
                  >
                    Get Directions
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl overflow-hidden card-shadow">
              <div className="h-96 lg:h-full">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="Leo Club Colombo Meeting Location"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${locationDetails.coordinates}&z=16&output=embed`}
                  className="border-0"
                />
              </div>
            </div>
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            {meetingSchedule.map((meeting) => (
              <div
                key={meeting.id}
                className="bg-card rounded-xl p-8 card-shadow hover:card-shadow-hover transition-smooth"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-3 h-3 rounded-full ${meeting.isRecurring ? 'bg-accent' : 'bg-secondary'}`} />
                      <h3 className="text-xl font-semibold text-card-foreground">
                        {meeting.type}
                      </h3>
                      {meeting.isRecurring && (
                        <span className="bg-accent/10 text-accent px-2 py-1 rounded-full text-xs font-medium">
                          Recurring
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={18} className="text-primary" />
                        <span className="text-sm text-muted-foreground">{meeting.day}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={18} className="text-primary" />
                        <span className="text-sm text-muted-foreground">{meeting.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="MapPin" size={18} className="text-primary" />
                        <span className="text-sm text-muted-foreground">{meeting.location}</span>
                      </div>
                    </div>
                    
                    <p className="text-muted-foreground">
                      {meeting.description}
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="outline"
                      iconName="Calendar"
                      iconPosition="left"
                      onClick={() => handleRSVP(meeting.type)}
                    >
                      RSVP
                    </Button>
                    <Button
                      variant="ghost"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => {
                        const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(meeting.type)}&details=${encodeURIComponent(meeting.description)}&location=${encodeURIComponent(meeting.location)}`;
                        window.open(calendarUrl, '_blank');
                      }}
                    >
                      Add to Calendar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Guest Information Tab */}
        {activeTab === 'guest' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-8 card-shadow">
                <h3 className="text-xl font-semibold text-card-foreground mb-6 flex items-center gap-3">
                  <Icon name="UserCheck" size={24} className="text-primary" />
                  Guest Attendance Policy
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="Check" size={14} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-card-foreground">Open Meetings</p>
                      <p className="text-sm text-muted-foreground">Regular meetings are open to prospective members and community partners</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="Check" size={14} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-card-foreground">Prior Registration</p>
                      <p className="text-sm text-muted-foreground">Please RSVP 24 hours in advance for catering and seating arrangements</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="Check" size={14} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-card-foreground">Age Requirement</p>
                      <p className="text-sm text-muted-foreground">Guests should be between 18-30 years old to align with Leo Club membership criteria</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="Check" size={14} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-card-foreground">Dress Code</p>
                      <p className="text-sm text-muted-foreground">Smart casual attire is recommended for all meetings</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl p-8 card-shadow">
                <h3 className="text-xl font-semibold text-card-foreground mb-6 flex items-center gap-3">
                  <Icon name="Clock" size={24} className="text-primary" />
                  What to Expect
                </h3>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-primary pl-4">
                    <p className="font-medium text-card-foreground">Welcome & Introductions</p>
                    <p className="text-sm text-muted-foreground">First 15 minutes for networking and guest introductions</p>
                  </div>
                  
                  <div className="border-l-4 border-secondary pl-4">
                    <p className="font-medium text-card-foreground">Project Updates</p>
                    <p className="text-sm text-muted-foreground">Current community service initiatives and impact reports</p>
                  </div>
                  
                  <div className="border-l-4 border-accent pl-4">
                    <p className="font-medium text-card-foreground">Planning Session</p>
                    <p className="text-sm text-muted-foreground">Collaborative planning for upcoming activities and events</p>
                  </div>
                  
                  <div className="border-l-4 border-warning pl-4">
                    <p className="font-medium text-card-foreground">Q&A Session</p>
                    <p className="text-sm text-muted-foreground">Open discussion about membership and club activities</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-8 card-shadow">
              <h3 className="text-xl font-semibold text-card-foreground mb-6 flex items-center gap-3">
                <Icon name="UserPlus" size={24} className="text-primary" />
                Register as Guest
              </h3>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="+94 77 123 4567"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Meeting to Attend *
                  </label>
                  <select className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                    <option value="">Select a meeting</option>
                    <option value="regular">Regular Meeting - Next Saturday</option>
                    <option value="orientation">New Member Orientation - Jan 28</option>
                    <option value="community">Community Service Day - Next Sunday</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    How did you hear about us?
                  </label>
                  <select className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent">
                    <option value="">Please select</option>
                    <option value="social-media">Social Media</option>
                    <option value="friend">Friend/Member Referral</option>
                    <option value="website">Website</option>
                    <option value="event">Community Event</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Additional Message
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Any questions or special requirements?"
                  />
                </div>
                
                <Button
                  variant="default"
                  iconName="Send"
                  iconPosition="left"
                  fullWidth
                  className="mt-6"
                >
                  Register for Meeting
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MeetingInformation;